/**
 * useCookieConsent.js
 *
 * Manages cookie consent state in localStorage.
 * Key: 'nc_cookie_consent'
 * Value: { analytics: boolean, timestamp: number, version: string }
 *
 * GDPR/CCPA: consent is opt-in, granular, timestamped, and versioned.
 * If the consent version stored doesn't match CONSENT_VERSION, the banner
 * is shown again (useful when cookie policy changes).
 */

import { useState, useEffect, useCallback } from 'react';
import { initGA } from '../utils/analytics';

const CONSENT_KEY     = 'nc_cookie_consent';
const CONSENT_VERSION = '1.0';          // bump this when policy changes

const readConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null; // outdated — re-ask
    return parsed;
  } catch {
    return null;
  }
};

const writeConsent = (analytics) => {
  const record = { analytics, timestamp: Date.now(), version: CONSENT_VERSION };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  return record;
};

/**
 * @returns {{
 *   showBanner: boolean,
 *   consentGiven: boolean | null,   // null = not yet decided
 *   acceptAnalytics: () => void,
 *   declineAnalytics: () => void,
 *   resetConsent: () => void,       // for a "manage cookies" link
 * }}
 */
const useCookieConsent = () => {
  const [state, setState] = useState(() => {
    const existing = readConsent();
    return {
      showBanner:   existing === null,
      consentGiven: existing?.analytics ?? null,
    };
  });

  // Initialize GA4 if consent was already given in a previous session
  useEffect(() => {
    if (state.consentGiven === true) {
      initGA();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acceptAnalytics = useCallback(() => {
    writeConsent(true);
    initGA();
    setState({ showBanner: false, consentGiven: true });
  }, []);

  const declineAnalytics = useCallback(() => {
    writeConsent(false);
    setState({ showBanner: false, consentGiven: false });
  }, []);

  const resetConsent = useCallback(() => {
    localStorage.removeItem(CONSENT_KEY);
    setState({ showBanner: true, consentGiven: null });
  }, []);

  return { ...state, acceptAnalytics, declineAnalytics, resetConsent };
};

export default useCookieConsent;
